import styled, { css } from 'styled-components';

import resets from '../../../../assets/styled/resets';
import { newDatePickerTokens } from '../../../../tokens/components/NewDatePicker/tokens';
import { controllerTokens } from '../../../../tokens/components/Controller/tokens';
import { colorTokens } from '../../../../tokens/globals/tokens';
import { borderRadius } from '../../Controller/styles';

import ClickableAffix from '../../ClickableAffix/ClickableAffix';
import { CalendarButton } from './CalendarButton/CalendarButton';

export const CalendarPanel = styled.div`
  display: flex;
  margin: 12px 0;
`;

export const CalendarTable = styled.table`
  width: 100%;
`;

export const CalendarControl = styled.div`
  margin: 0 4px;
  position: relative;
  width: 100%;
`;

export const CalendarControlInput = styled.input`
  border: 0;
  color: ${newDatePickerTokens.ContainerFontColorBaseDefault};
  width: 100%;
  height: 26px;
  line-height: 1.4;
  padding: 4px;
  text-align: center;
  vertical-align: baseline;

  &:focus-visible {
    background-color: ${newDatePickerTokens.PanelButtonBackgroundColorBaseDefault};
    box-shadow: inset 0 -1px 0 0 ${newDatePickerTokens.PanelButtonBorderColorBaseFocus};
  }
`;

// TODO: This should inherit Button styles at some point
export const CalendarArrow = styled.button`
  align-items: center;
  appearance: none;
  background-color: ${newDatePickerTokens.PanelButtonBackgroundColorBaseDefault};
  border-width: ${newDatePickerTokens.PanelButtonBorderWidthBaseDefault};
  border-radius: ${newDatePickerTokens.PanelButtonBorderRadiusBaseDefault};
  border-style: solid;
  border-color: ${newDatePickerTokens.PanelButtonBorderColorBaseDefault};
  color: ${newDatePickerTokens.PanelButtonColorBaseDefault};
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: 0 2px;
  transition-duration: 60ms;
  transition-property: background-color, color, border-color, box-shadow;
  transition-timing-function: ease-in;
  white-space: nowrap;
  width: auto;

  &:hover {
    border-color: ${newDatePickerTokens.PanelButtonBorderColorBaseHover};
    color: ${newDatePickerTokens.PanelButtonColorBaseHover};
  }

  &:focus-visible {
    outline-width: 1px;
    box-shadow: 0 0 0 2px rgba(0, 116, 192, 0.333);
  }
`;

export const CalendarControlSelect = styled.select`
  ${resets.BrowserSelect}
  cursor: pointer;
  border: 0;
  height: 26px;
  line-height: 1.4;
  padding: 4px;
  text-align: center;
  vertical-align: baseline;
  border-radius: 0;
  width: 100%;
  background-color: ${newDatePickerTokens.PanelButtonBackgroundColorBaseDefault};

  &:hover {
    box-shadow: inset 0 -1px 0 0 ${newDatePickerTokens.PanelButtonBorderColorBaseHover};
  }

  &:focus-visible {
    background-color: ${newDatePickerTokens.PanelButtonBackgroundColorBaseDefault};
    box-shadow: inset 0 -1px 0 0 ${newDatePickerTokens.PanelButtonBorderColorBaseFocus};

    + input {
      box-shadow: inset 0 -1px 0 0 ${newDatePickerTokens.PanelButtonBorderColorBaseFocus};
    }
  }
`;

export const CalendarControlSelectArrow = styled.div`
  color: ${newDatePickerTokens.ContainerFontColorBaseDefault};
  display: inline-flex;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

interface CalendarPanelSectionTypes {
  marginLeft?: string;
  flex?: number;
}
export const CalendarPanelSection = styled.div<CalendarPanelSectionTypes>`
  display: flex;

  ${props =>
    props.marginLeft &&
    `
    margin-left: ${props.marginLeft};
  `}

  ${props =>
    props.flex &&
    `
    flex: ${props.flex};
  `}
`;

// This fake input is used as a fix for the inconsistent rendering of the select element across browsers.
// The height and position of this element is specifically set to show the underline focus state of the select element.
export const CalendarControlFake = styled.input`
  ${resets.BrowserInput}
  background-color: ${newDatePickerTokens.PanelButtonBackgroundColorBaseDefault};
  color: ${newDatePickerTokens.ContainerFontColorBaseDefault};
  box-shadow: inset 0 -1px 0 0 ${newDatePickerTokens.PanelButtonBorderColorBaseDefault};
  border: 0;
  bottom: 2px;
  left: 0;
  padding-right: 15px;
  pointer-events: none;
  position: absolute;
  right: 0;
  width: 100%;
  height: 22px;
  line-height: 1.4;
  text-align: center;
  vertical-align: baseline;
`;

export const TableCell = styled.td`
  text-align: center;
  width: 14.2%;
`;

export const DayButton = styled.button<{ disabled: boolean; selected: boolean }>`
  background-color: transparent;
  border-color: ${newDatePickerTokens.DaysBorderColorBaseDefault};
  border-style: solid;
  border-width: ${newDatePickerTokens.DaysBorderWidthBaseDefault};
  border-radius: ${newDatePickerTokens.DaysBorderRadiusBaseDefault};
  line-height: 1;
  cursor: pointer;
  padding: ${newDatePickerTokens.DaysSpacingPaddingBaseDefault} 0;
  width: 99%;

  &:hover {
    border-color: ${newDatePickerTokens.DaysBorderColorBaseHover};
  }

  ${props =>
    props.selected &&
    css`
      background-color: ${newDatePickerTokens.DaysBackgroundColorSelectedDefault};

      &:hover,
      &:focus {
        background-color: ${newDatePickerTokens.DaysBackgroundColorSelectedHover};
      }
    `}

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export const CalendarClickableAffix = styled(ClickableAffix)<Partial<CalendarButton>>`
  margin: 0;

  ${({ calendarOpen, invalid }) =>
    calendarOpen &&
    !invalid &&
    css`
      &,
      &:hover {
        border-color: ${controllerTokens.ContainerBorderColorBaseKeyboardFocus};
        box-shadow: -1px 0 0 0 ${controllerTokens.ContainerBorderColorBaseKeyboardFocus};
      }
    `}

  ${({ calendarOpen, invalid }) =>
    calendarOpen &&
    invalid &&
    css`
      &,
      &:hover {
        border-color: ${controllerTokens.ContainerBorderColorInvalidDefault};
        box-shadow: -1px 0 0 0 ${controllerTokens.ContainerBorderColorInvalidDefault};
      }
    `}
`;

export const Item = styled.li`
  flex: 1;
  padding: 4px 0;

  &:not(:first-child) {
    border-top: 1px solid ${colorTokens.GlobalBrandGray30};
  }
`;

export const ShortcutList = styled.ul`
  display: flex;
  flex-direction: column;
  height: calc(100% + 8px);
  margin: -4px 0;
  width: 100%;
`;

export const ShortcutHint = styled.span`
  background-color: ${colorTokens.GlobalBrandGray10};
  border-radius: ${borderRadius};
  bottom: 0;
  display: block;
  margin: 2px;
  padding: 0 4px;
  position: absolute;
  right: 0;
`;

export const ShortcutButton = styled.button`
  background-color: transparent;
  border: 0;
  border-radius: 4px;
  color: ${colorTokens.GlobalBrandBlack};
  cursor: pointer;
  height: 100%;
  line-height: 1.4;
  padding: 8px 12px;
  position: relative;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus {
    background-color: ${colorTokens.GlobalBrandGray10};

    ${ShortcutHint} {
      background-color: ${colorTokens.GlobalBrandGray20};
    }
  }
`;
